import { useAppRouteParams } from "@/AppRoutes";
import { postSetPriorityVersion } from "@/api/versionCategory";
import {
  BulkActionRequest,
  useBulkActionToast,
} from "@/components/AppProvider/AppNotificationProvider/useBulkActionToast";
import { useVersionCategories } from "@/components/InOrganizationProvider/VersionCategoriesProvider";
import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";
import React, { useCallback, useEffect, useRef } from "react";
import { Version, useUpdateVersion } from "../../../../../domain/Version";

/**
 * 複数バージョンのカテゴリを即時更新し、完了後 Toast で結果を通知するフック。
 * 処理中 Toast は表示せず、完了後に "元に戻す" ボタン付き Toast を表示する。
 * updateCategoryRequest.phase が "requested" かつ itemIds.length > 0 のときに実行する。
 */

type UndoData = {
  oldCategoryId: string;
  wasPriorityVersion: boolean;
};
const useTranslation = translation.use(nsVersion);

export const useUpdateCategorySomeAction = ({
  versions,
  updateCategoryRequest,
  setUpdateCategoryRequest,
  setSelectedVersionIds,
  newCategoryId,
}: {
  versions: Version[];
  updateCategoryRequest: BulkActionRequest;
  setUpdateCategoryRequest: React.Dispatch<
    React.SetStateAction<BulkActionRequest>
  >;
  setSelectedVersionIds: React.Dispatch<React.SetStateAction<string[]>>;
  newCategoryId: string | null;
}) => {
  const { t } = useTranslation();
  const { organizationId } = useAppRouteParams();
  const updateVersion = useUpdateVersion();
  const { versionCategories, setVersionCategories } = useVersionCategories();
  const { runWithToast } = useBulkActionToast({
    success: (count) => t.version.updateCategory.success({ count }),
    error: (count) => t.version.updateCategory.error({ count }),
  });

  // versionsの最新値を常に参照できるようにrefで保持
  const versionsRef = useRef(versions);
  useEffect(() => {
    versionsRef.current = versions;
  }, [versions]);

  const runUpdate = useCallback(
    async (
      version: Version,
      oldCategoryId: string
    ): Promise<{ wasPriorityVersion: boolean }> => {
      const shouldClearPriorityVersion = Boolean(
        oldCategoryId &&
          oldCategoryId !== newCategoryId &&
          versionCategories?.find((cat) => cat.id === oldCategoryId)
            ?.priority_version_id === version.id
      );

      // バージョンのカテゴリを更新（先に実行）
      await updateVersion(
        {
          ...version,
          category: newCategoryId ?? "",
        },
        {}
      );

      // updateVersion成功後、バージョンが元のカテゴリの優先バージョンに設定されていた場合のみ、優先バージョンをクリアする
      if (shouldClearPriorityVersion && oldCategoryId) {
        try {
          await postSetPriorityVersion({
            versionCategoryId: oldCategoryId,
            organizationId,
            versionId: undefined, // undefinedでクリア
          });

          // ローカルのカテゴリ情報も更新して、UIに正しく反映させる
          setVersionCategories((prev) => {
            if (!prev) return prev;
            return prev.map((cat) =>
              cat.id === oldCategoryId
                ? { ...cat, priority_version_id: null }
                : cat
            );
          });
        } catch (clearError) {
          // 優先バージョンのクリアに失敗した場合、カテゴリ移動をロールバック
          try {
            await updateVersion(
              {
                ...version,
                category: oldCategoryId,
              },
              {}
            );
          } catch (rollbackError) {
            // ロールバックも失敗した場合
            throw new Error(
              t.version.updateCategory.clearPriorityRollbackError({
                clearError:
                  clearError instanceof Error
                    ? clearError.message
                    : String(clearError),
                rollbackError:
                  rollbackError instanceof Error
                    ? rollbackError.message
                    : String(rollbackError),
              })
            );
          }

          // ロールバックが成功した場合
          throw new Error(t.version.updateCategory.clearPriorityError());
        }
      }

      return { wasPriorityVersion: shouldClearPriorityVersion };
    },
    [
      newCategoryId,
      versionCategories,
      updateVersion,
      organizationId,
      setVersionCategories,
      t,
    ]
  );

  const undoUpdate = useCallback(
    async (undoData: UndoData, version: Version) => {
      // カテゴリ変更後に他の更新が入っている可能性があるため、最新のバージョンを起点に戻す
      const latestVersion =
        versionsRef.current.find((v) => v.id === version.id) ?? version;

      await updateVersion(
        {
          ...latestVersion,
          category: undoData.oldCategoryId,
        },
        {}
      );

      if (!undoData.wasPriorityVersion || !undoData.oldCategoryId) return;

      await postSetPriorityVersion({
        versionCategoryId: undoData.oldCategoryId,
        organizationId,
        versionId: version.id,
      });

      setVersionCategories((prev) => {
        if (!prev) return prev;
        return prev.map((cat) =>
          cat.id === undoData.oldCategoryId
            ? { ...cat, priority_version_id: version.id }
            : cat
        );
      });
    },
    [updateVersion, organizationId, setVersionCategories]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      updateCategoryRequest.phase !== "requested" ||
      updateCategoryRequest.itemIds.length === 0
    )
      return;

    const targetVersions = versions.filter((v) =>
      updateCategoryRequest.itemIds.includes(v.id)
    );
    if (targetVersions.length === 0) {
      setUpdateCategoryRequest({ phase: "idle", itemIds: [] });
      return;
    }

    // 即時 in_progress へ遷移してリマウント時の二重実行を防ぐ
    setUpdateCategoryRequest({
      phase: "in_progress",
      itemIds: updateCategoryRequest.itemIds,
    });

    const run = async () => {
      const { failedIds } = await runWithToast<Version, UndoData>(
        targetVersions,
        async (version) => {
          const oldCategoryId = version.category;
          const { wasPriorityVersion } = await runUpdate(
            version,
            oldCategoryId
          );
          return { oldCategoryId, wasPriorityVersion };
        },
        { undoItem: undoUpdate }
      );

      setSelectedVersionIds(failedIds);
      setUpdateCategoryRequest({ phase: "idle", itemIds: [] });
    };

    run();
  }, [updateCategoryRequest.phase]);
};
